import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from './localStorage.service';
import { Event as EventList, HqEvent } from 'src/app/pusher-event';
import { PusherEventService } from './pusher-event.service';
import { io, Socket } from 'socket.io-client';
import { ApiService } from './api.service';

declare const Pusher: any;

type SocketMode = 'pusher' | 'rtc' | null;
type SocketInitKind = 'pos' | 'multi' | 'hq' | null;

@Injectable({
  providedIn: 'root',
})
export class CommonSocketService {
  // Pusher properties
  private pusher: any;
  private restaurantChannel: any;
  private multiRestChannels: any;
  private hqChannel: any;

  // RTC Socket.IO properties
  private rtcSocket: Socket | null = null;
  private desiredRooms = new Set<string>();
  private rtcBoundEvents = new Set<string>();

  // Socket mode
  private socketMode: SocketMode = null;
  private lastInitKind: SocketInitKind = null;
  private lastMultiRestIds: string[] = [];

  constructor(
    private _localStorage: LocalStorageService,
    private _pusherEvent: PusherEventService,
    private _apiService: ApiService
  ) {
    this.watchRestaurantDetailsForSocketMode();
    this.initializeSocketMode();
  }

  /**
   * Prefer restaurant_details / restaurant_settings from get.php.
   * Do not trust a leftover socket_mode key by itself.
   */
  private initializeSocketMode(): void {
    const fromDetails = this.resolveSocketModeFromRestaurantDetails();
    if (fromDetails) {
      this.applySocketMode(fromDetails, { reconnect: false });
      return;
    }

    const restaurantId = this._localStorage.get('restaurant_id');
    if (restaurantId) {
      this.fetchSocketModeFromRestaurantDetails(restaurantId);
    }
  }

  private watchRestaurantDetailsForSocketMode(): void {
    this._localStorage
      .watch('restaurant_details')
      .pipe(distinctUntilChanged())
      .subscribe((raw) => {
        const mode = this.extractSocketMode(this.parseStoredJson(raw));
        if (mode) {
          this.applySocketMode(mode);
        }
      });

    this._localStorage
      .watch('restaurant_settings')
      .pipe(distinctUntilChanged())
      .subscribe((raw) => {
        const mode = this.extractSocketMode(this.parseStoredJson(raw));
        if (mode) {
          this.applySocketMode(mode);
        }
      });

    this._localStorage
      .watch('restaurant_id')
      .pipe(distinctUntilChanged())
      .subscribe((restaurantId) => {
        if (restaurantId) {
          this.fetchSocketModeFromRestaurantDetails(restaurantId);
          return;
        }
        this.socketMode = null;
        this._localStorage.remove('socket_mode');
      });
  }

  /**
   * POST /api/restaurant/details/get.php — same payload as partner-app restaurant details.
   */
  private fetchSocketModeFromRestaurantDetails(restaurantId?: string): void {
    const id = restaurantId || this._localStorage.get('restaurant_id');
    if (!id) {
      return;
    }

    const param = new FormData();
    param.append('restaurant_id', id);
    this._apiService.fetchRestaurantDetails(param).subscribe(
      (response) => {
        if (!response?.status || !response?.data) {
          console.warn('Restaurant details get.php did not return data for socket_mode.');
          return;
        }
        const mode = this.extractSocketMode(response.data) || 'pusher';
        this.applySocketMode(mode);
      },
      (error) => {
        console.error('Error fetching restaurant details for socket_mode:', error);
      }
    );
  }

  private parseStoredJson(value: any): any {
    if (!value) {
      return null;
    }
    if (typeof value === 'object') {
      return value;
    }
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private extractSocketMode(source: any): SocketMode {
    if (!source) {
      return null;
    }
    const settings = source.data?.settings || source.settings || source;
    const mode = settings?.global?.socket_mode;
    return mode === 'pusher' || mode === 'rtc' ? mode : null;
  }

  private resolveSocketModeFromRestaurantDetails(): SocketMode {
    const fromDetails = this.extractSocketMode(
      this.parseStoredJson(this._localStorage.get('restaurant_details'))
    );
    if (fromDetails) {
      return fromDetails;
    }
    return this.extractSocketMode(
      this.parseStoredJson(this._localStorage.get('restaurant_settings'))
    );
  }

  private applySocketMode(
    mode: SocketMode,
    options: { reconnect?: boolean } = {}
  ): void {
    const next = mode === 'rtc' || mode === 'pusher' ? mode : null;
    if (!next) {
      return;
    }
    const previous = this.socketMode;
    this.socketMode = next;
    this._localStorage.set('socket_mode', next);
    if (previous !== next) {
      console.log(
        `%cSocket mode set from restaurant details: ${next}`,
        'color: green; font-weight: bold; font-size: 12px'
      );
    }
    if (options.reconnect !== false && previous && previous !== next && this.lastInitKind) {
      this.reconnectAfterModeChange();
    }
  }

  /**
   * Get current socket mode from restaurant details, then the in-memory value.
   */
  getSocketMode(): SocketMode {
    return this.resolveSocketModeFromRestaurantDetails() || this.socketMode;
  }

  /**
   * Watch for events on the socket connection
   * This method works for both Pusher and RTC modes
   */
  watchEvent(eventKey: string): Observable<any> {
    return this._pusherEvent.watchEvent(eventKey);
  }

  /**
   * Initialize socket for POS (single restaurant)
   */
  initSocketForPOS(): void {
    this.lastInitKind = 'pos';
    this.ensureModeThenConnect(() => {
      if (this.getSocketMode() === 'rtc') {
        this.initRtcForPOS();
      } else if (this.getSocketMode() === 'pusher') {
        this.initPusherForPOS();
      }
    });
  }

  /**
   * Initialize socket for multiple restaurant channels
   */
  initSocketForMultipleChannels(rest_ids: string[]): void {
    this.lastInitKind = 'multi';
    this.lastMultiRestIds = rest_ids || [];
    this.ensureModeThenConnect(() => {
      if (this.getSocketMode() === 'rtc') {
        this.initRtcForMultipleChannels(this.lastMultiRestIds);
      } else if (this.getSocketMode() === 'pusher') {
        this.initPusherForMultipleChannels(this.lastMultiRestIds);
      }
    });
  }

  /**
   * Initialize socket for HQ
   */
  initSocketForHQ(): void {
    this.lastInitKind = 'hq';
    this.ensureModeThenConnect(() => {
      if (this.getSocketMode() === 'rtc') {
        this.initRtcForHQ();
      } else if (this.getSocketMode() === 'pusher') {
        this.initPusherForHQ();
      }
    });
  }

  /**
   * Disconnect socket
   */
  disconnectSocket(): void {
    this.lastInitKind = null;
    this.lastMultiRestIds = [];
    this.disconnectAllTransports();
  }

  private ensureModeThenConnect(connect: () => void): void {
    const fromDetails = this.resolveSocketModeFromRestaurantDetails();
    if (fromDetails) {
      this.applySocketMode(fromDetails, { reconnect: false });
      connect();
      return;
    }

    const restaurantId = this._localStorage.get('restaurant_id');
    if (!restaurantId) {
      console.warn('No restaurant_id found. Skipping socket init until restaurant details load.');
      return;
    }

    const param = new FormData();
    param.append('restaurant_id', restaurantId);
    this._apiService.fetchRestaurantDetails(param).subscribe(
      (response) => {
        const mode = this.extractSocketMode(response?.data) || 'pusher';
        this.applySocketMode(mode, { reconnect: false });
        connect();
      },
      (error) => {
        console.error('Error fetching restaurant details before socket init:', error);
      }
    );
  }

  private reconnectAfterModeChange(): void {
    this.disconnectAllTransports();
    if (this.lastInitKind === 'pos') {
      this.initSocketForPOS();
    } else if (this.lastInitKind === 'multi') {
      this.initSocketForMultipleChannels(this.lastMultiRestIds);
    } else if (this.lastInitKind === 'hq') {
      this.initSocketForHQ();
    }
  }

  private disconnectAllTransports(): void {
    this.disconnectPusher();
    this.disconnectRtc();
  }

  // ==================== PUSHER METHODS (REUSED) ====================

  private initPusherForPOS(): void {
    this.initializePusherInstance();
    const restId = this._localStorage.get('restaurant_id');
    this.restaurantChannel = this.pusher.subscribe(restId);

    // Listening all pusher event
    for (const msg_event in EventList) {
      this.onRestaurantEvent(EventList[msg_event]).subscribe((res) => {
        this._pusherEvent.bindEvent(EventList[msg_event], res);
      });
    }
  }

  private initPusherForMultipleChannels(rest_ids: string[]): void {
    this.initializePusherInstance();
    this.multiRestChannels = {};
    (rest_ids || []).forEach((rest_id) => {
      this.multiRestChannels[rest_id] = this.pusher.subscribe(rest_id);
      for (const msg_event in EventList) {
        this.onFcRestEvent(
          EventList[msg_event],
          this.multiRestChannels[rest_id]
        ).subscribe((res) => {
          this._pusherEvent.bindEvent(EventList[msg_event], res);
        });
      }
    });
  }

  private initPusherForHQ(): void {
    const hqId = this._localStorage.get('hq_id');
    if (hqId) {
      this.initializePusherInstance();
      this.hqChannel = this.pusher.subscribe(hqId);
      for (const msg_event in HqEvent) {
        this.onHQEvent(HqEvent[msg_event]).subscribe((res) => {
          this._pusherEvent.bindEvent(HqEvent[msg_event], res);
        });
      }
    } else {
      console.warn('No HQ ID found in localStorage.');
    }
  }

  private disconnectPusher(): void {
    if (this.pusher) this.pusher.disconnect();
    this.pusher = null;
    console.log(
      '%cPusher disconnected',
      'color: red; font-family:monospace; font-size: 20px'
    );
  }

  private onRestaurantEvent(event: string): Observable<any> {
    return new Observable<Event>((observer) => {
      this.restaurantChannel.bind(event, (data) => observer.next(data));
    });
  }

  private onFcRestEvent(event: string, rest_id: any): Observable<any> {
    return new Observable<Event>((observer) => {
      rest_id.bind(event, (data) => observer.next(data));
    });
  }

  private onHQEvent(event: string): Observable<any> {
    return new Observable<Event>((observer) => {
      this.hqChannel.bind(event, (data) => observer.next(data));
    });
  }

  private initializePusherInstance(): void {
    if (!this.pusher) {
      this.pusher = new Pusher(environment.pusherConfig.key, {
        cluster: environment.pusherConfig.cluster,
        encrypted: true,
      });

      // Log when Pusher connects successfully
      this.pusher.connection.bind('connected', function () {
        console.log(
          '%cPusher connected successfully!',
          'color: green; font-weight: bold; font-family:monospace; font-size: 14px'
        );
      });

      // Log for any errors occurring in the connection
      this.pusher.connection.bind('error', function (error) {
        console.error(
          '%cError occurred in Pusher connection:',
          'color: red; font-weight: bold',
          error
        );
      });

      // Log when the connection is disconnected
      this.pusher.connection.bind('disconnected', function () {
        this.pusher = null;
        console.log(
          '%cPusher connection disconnected.',
          'color: orange; font-weight: bold; font-family:monospace; font-size: 14px'
        );
      });

      // Log when the connection is attempting to reconnect
      this.pusher.connection.bind('connecting', function () {
        console.log(
          '%cPusher is attempting to reconnect...',
          'color: yellow; font-family:monospace; font-size: 14px'
        );
      });
    }
  }

  // ==================== RTC SOCKET.IO METHODS ====================

  private initRtcForPOS(): void {
    const restId = this._localStorage.get('restaurant_id');
    if (!restId) {
      console.warn('No restaurant_id found for RTC POS subscribe.');
      return;
    }
    this.desiredRooms = new Set([restId]);
    if (!this.ensureRtcSocket()) {
      return;
    }
    this.bindRtcNamedEvents(this.namedEventValues(EventList));
    this.subscribeDesiredRooms();
  }

  private initRtcForMultipleChannels(rest_ids: string[]): void {
    this.desiredRooms = new Set((rest_ids || []).filter(Boolean));
    if (!this.ensureRtcSocket()) {
      return;
    }
    this.bindRtcNamedEvents(this.namedEventValues(EventList));
    this.subscribeDesiredRooms();
  }

  private initRtcForHQ(): void {
    const hqId = this._localStorage.get('hq_id');
    if (!hqId) {
      console.warn('No HQ ID found in localStorage for RTC.');
      return;
    }
    this.desiredRooms.add(hqId);
    if (!this.ensureRtcSocket()) {
      return;
    }
    this.bindRtcNamedEvents(this.namedEventValues(HqEvent));
    this.subscribeDesiredRooms();
  }

  private disconnectRtc(): void {
    if (this.rtcSocket) {
      this.rtcSocket.removeAllListeners();
      this.rtcSocket.disconnect();
      this.rtcSocket = null;
    }
    this.desiredRooms.clear();
    this.rtcBoundEvents.clear();
    console.log(
      '%cRTC Socket.IO disconnected',
      'color: red; font-family:monospace; font-size: 20px'
    );
  }

  private namedEventValues(list: object): string[] {
    return Object.keys(list)
      .map((key) => list[key])
      .filter((value) => typeof value === 'string' && value !== 'event');
  }

  private bindRtcNamedEvents(eventNames: string[]): void {
    const socket = this.rtcSocket;
    if (!socket) {
      return;
    }
    eventNames.forEach((eventName) => {
      if (this.rtcBoundEvents.has(eventName)) {
        return;
      }
      this.rtcBoundEvents.add(eventName);
      socket.on(eventName, (data) => {
        this.forwardRtcPayload(eventName, data);
      });
    });
  }

  private forwardRtcPayload(eventName: string, data: any): void {
    if (!data) {
      return;
    }
    const topic = data.topic;
    const restaurantId = data.restaurant_id || data.data?.restaurant_id;
    const hqId = data.hq_id;
    if (this.desiredRooms.size) {
      const inSubscribedRoom =
        (topic && this.desiredRooms.has(String(topic))) ||
        (restaurantId && this.desiredRooms.has(String(restaurantId))) ||
        (hqId && this.desiredRooms.has(String(hqId)));
      if (!inSubscribedRoom) {
        return;
      }
    }
    this._pusherEvent.bindEvent(eventName, data);
  }

  private subscribeDesiredRooms(): void {
    if (!this.rtcSocket?.connected) {
      return;
    }
    const rooms = Array.from(this.desiredRooms);
    if (!rooms.length) {
      return;
    }
    this.rtcSocket.emit('subscribe', { rooms }, (ack?: { joined?: string[]; denied?: string[] }) => {
      console.log(
        '%cRTC subscribe',
        'color: green; font-family:monospace; font-size: 12px',
        ack || rooms
      );
    });
  }

  private ensureRtcSocket(): boolean {
    const token = this._localStorage.get('token');
    if (!token) {
      console.warn('RTC Socket.IO requires a login JWT in localStorage.');
      return false;
    }

    if (this.rtcSocket) {
      return true;
    }

    this.rtcSocket = io(environment.rtcUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      withCredentials: true,
    });

    this.rtcSocket.on('connect', () => {
      console.log(
        '%cRTC Socket.IO connected successfully!',
        'color: green; font-weight: bold; font-family:monospace; font-size: 14px'
      );
      this.subscribeDesiredRooms();
    });

    this.rtcSocket.on('connect_error', (error: Error) => {
      console.error(
        '%cError occurred in RTC Socket.IO connection:',
        'color: red; font-weight: bold',
        error
      );
    });

    this.rtcSocket.on('disconnect', (reason: string) => {
      console.log(
        `%cRTC Socket.IO disconnected: ${reason}`,
        'color: orange; font-weight: bold; font-family:monospace; font-size: 14px'
      );
    });

    this.rtcSocket.on('reconnect_attempt', (attemptNumber: number) => {
      console.log(
        `%cRTC Socket.IO attempting to reconnect (attempt ${attemptNumber})...`,
        'color: yellow; font-family:monospace; font-size: 14px'
      );
    });

    this.rtcSocket.on('reconnect', (attemptNumber: number) => {
      console.log(
        `%cRTC Socket.IO reconnected after ${attemptNumber} attempts!`,
        'color: green; font-weight: bold; font-family:monospace; font-size: 14px'
      );
      this.subscribeDesiredRooms();
    });

    return true;
  }
}
